import { useState, useEffect } from 'react';
import {
  Box,
  Collapse,
  Drawer,
  IconButton,
  List,
  ListItem,
  Typography,
  Button
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import PropTypes from 'prop-types';
import { motion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

import THEME from '../../constants/theme';
import ROUTES from '../../config/routes';
import { useNavigation } from '../../context/NavigationContext';
import { glassEffect } from '../../styles';
import { getLogo, pdfFiles } from '../../utils';
import { navigationMenu } from '../../config/navigation';

const NavigationMenuMobile = () => {
  const location = useLocation();
  const { isMobileMenuOpen, setIsMobileMenuOpen, navigateToSection } =
    useNavigation();
  const [expandedItems, setExpandedItems] = useState({});
  const currentYear = new Date().getFullYear();

  const menuItems = navigationMenu.filter(item => item.title !== 'Portal');

  // Auto-expand menu based on current route
  useEffect(() => {
    const currentPath = location.pathname;
    const normalizedPath =
      currentPath !== '/' && currentPath.endsWith('/')
        ? currentPath.slice(0, -1)
        : currentPath;

    let autoExpandTarget = null;

    if (normalizedPath.startsWith(ROUTES.ABOUT_US.ROOT)) {
      autoExpandTarget = 'About Us';
    } else if (normalizedPath.startsWith(ROUTES.STRATEGIES.ROOT)) {
      autoExpandTarget = 'Strategies';
    } else if (normalizedPath.startsWith(ROUTES.HOW_TO_JOIN.ROOT)) {
      autoExpandTarget = 'How to Join';
    } else if (normalizedPath.startsWith(ROUTES.LEGAL.ROOT)) {
      autoExpandTarget = 'Legal';
    } else if (normalizedPath === ROUTES.LIBRARY) {
      autoExpandTarget = 'Library';
    }

    if (autoExpandTarget) {
      setExpandedItems(prev => ({ ...prev, [autoExpandTarget]: true }));
    }
  }, [location.pathname]);

  const toggleMobileMenu = () => setIsMobileMenuOpen(!isMobileMenuOpen);

  const handleScrollMobile = (id, path) => {
    setIsMobileMenuOpen(false);
    navigateToSection(id, path);
  };

  const handleMainItemClick = path => {
    setIsMobileMenuOpen(false);
    navigateToSection('hero', path);
  };

  const toggleExpanded = title => {
    setExpandedItems(prev => ({ ...prev, [title]: !prev[title] }));
  };

  const logoSrcForDrawer = getLogo('#000000');

  return (
    <>
      {!isMobileMenuOpen && (
        <IconButton
          onClick={toggleMobileMenu}
          sx={{
            position: 'fixed',
            top: { xs: 20, sm: 30 },
            right: { xs: 20, sm: 30 },
            zIndex: 10001,
            ...glassEffect({
              padding: '12px',
              borderRadius: '50%',
              backgroundOpacity: 0.15,
              borderOpacity: 0.3,
              blurStrength: 12
            }),
            color: THEME.COLORS.NEUTRAL_BLACK,
            transition: 'all 0.3s ease',
            '&:hover': {
              backgroundColor: 'rgba(255, 255, 255, 0.3)',
              backdropFilter: 'blur(15px)',
              transform: 'scale(1.05)'
            },
            display: {
              xs: 'flex',
              sm: 'flex',
              md: 'flex',
              lg: 'flex',
              xl: 'none'
            }
          }}
        >
          <MenuIcon />
        </IconButton>
      )}

      <Drawer
        anchor='right'
        open={isMobileMenuOpen}
        onClose={toggleMobileMenu}
        PaperProps={{
          sx: {
            width: '100%',
            maxWidth: { xs: '100%', sm: '380px' },
            backgroundColor: THEME.COLORS.NEUTRAL_WHITE,
            color: THEME.COLORS.NEUTRAL_BLACK,
            padding: '24px',
            paddingTop: '24px',
            display: 'flex',
            flexDirection: 'column',
            boxShadow: '-4px 0 20px rgba(0, 0, 0, 0.15)'
          }
        }}
      >
        {/* Header */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            mb: 3,
            pb: 2,
            borderBottom: `2px solid ${THEME.COLORS.NEUTRAL_GRAY_LIGHT}`
          }}
        >
          <Box sx={{ maxWidth: '140px' }}>
            <img
              src={logoSrcForDrawer}
              alt='MarQ Capital Logo'
              style={{ width: '100%', height: 'auto' }}
            />
          </Box>
          <IconButton
            onClick={toggleMobileMenu}
            sx={{
              color: THEME.COLORS.NEUTRAL_BLACK,
              '&:hover': {
                backgroundColor: THEME.COLORS.NEUTRAL_GRAY_LIGHT
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Portal Button at Top */}
        <Button
          variant='contained'
          onClick={() => {
            setIsMobileMenuOpen(false);
            handleMainItemClick(ROUTES.AUTH);
          }}
          sx={{
            mb: 3,
            py: 1.5,
            backgroundColor: THEME.COLORS.PRIMARY_BLUE,
            color: THEME.COLORS.NEUTRAL_WHITE,
            fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
            fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
            fontSize: '1rem',
            textTransform: 'none',
            borderRadius: '8px',
            boxShadow: `0 4px 12px ${THEME.COLORS.PRIMARY_BLUE}44`,
            '&:hover': {
              backgroundColor: THEME.COLORS.PRIMARY_BLUE_DARK,
              boxShadow: `0 6px 16px ${THEME.COLORS.PRIMARY_BLUE}66`
            }
          }}
        >
          Access Portal
        </Button>

        <List sx={{ flex: 1, overflow: 'auto' }}>
          {menuItems.map((item, index) => (
            <Box key={index} sx={{ mb: 1.5 }}>
              {item.hasDropdown ? (
                <>
                  <ListItem
                    sx={{
                      px: 2,
                      py: 1.5,
                      borderRadius: '8px',
                      backgroundColor: expandedItems[item.title]
                        ? THEME.COLORS.NEUTRAL_GRAY_LIGHT
                        : 'transparent',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        backgroundColor: THEME.COLORS.NEUTRAL_GRAY_LIGHT
                      }
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                        fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                        fontSize: '1.1rem',
                        flex: 1,
                        cursor: 'pointer',
                        color: THEME.COLORS.NEUTRAL_BLACK
                      }}
                      onClick={() => {
                        setIsMobileMenuOpen(false);
                        handleMainItemClick(item.path);
                      }}
                    >
                      {item.title}
                    </Typography>
                    {item.submenu && item.submenu.length > 0 && (
                      <IconButton
                        onClick={e => {
                          e.stopPropagation();
                          toggleExpanded(item.title);
                        }}
                        sx={{
                          ml: 1,
                          color: THEME.COLORS.PRIMARY_BLUE,
                          transition: 'all 0.2s'
                        }}
                        size='small'
                      >
                        <ExpandMoreIcon
                          sx={{
                            transform: expandedItems[item.title]
                              ? 'rotate(180deg)'
                              : 'rotate(0deg)',
                            transition: 'transform 0.3s ease'
                          }}
                        />
                      </IconButton>
                    )}
                  </ListItem>
                  {item.submenu && item.submenu.length > 0 && (
                    <Collapse
                      in={expandedItems[item.title]}
                      timeout='auto'
                      unmountOnExit
                    >
                      <List component='div' disablePadding>
                        {item.submenu.map((submenu, subIndex) => (
                          <ListItem
                            key={subIndex}
                            sx={{
                              pl: 5,
                              pr: 2,
                              py: 1,
                              cursor: 'pointer',
                              borderRadius: '6px',
                              transition: 'all 0.2s ease',
                              display: 'flex',
                              alignItems: 'center',
                              '&:hover': {
                                backgroundColor:
                                  THEME.COLORS.PRIMARY_BLUE_LIGHT + '33',
                                transform: 'translateX(4px)'
                              }
                            }}
                            onClick={() => {
                              setIsMobileMenuOpen(false);
                              if (submenu.id)
                                handleScrollMobile(submenu.id, item.path);
                              else if (submenu.isFile || submenu.pdfId)
                                window.open(
                                  submenu.path ||
                                    (submenu.pdfId
                                      ? pdfFiles[submenu.pdfId]
                                      : ''),
                                  '_blank',
                                  'noopener,noreferrer'
                                );
                              else if (submenu.path)
                                navigateToSection('hero', submenu.path);
                            }}
                          >
                            {(submenu.isFile || submenu.pdfId) && (
                              <FiberManualRecordIcon
                                sx={{
                                  fontSize: '0.4rem',
                                  color: THEME.COLORS.PRIMARY_BLUE,
                                  mr: 1.5
                                }}
                              />
                            )}
                            <Typography
                              sx={{
                                fontFamily:
                                  THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
                                fontSize: '0.95rem',
                                color: THEME.COLORS.NEUTRAL_GRAY_DARK
                              }}
                            >
                              {submenu.title}
                            </Typography>
                          </ListItem>
                        ))}
                      </List>
                    </Collapse>
                  )}
                </>
              ) : (
                <ListItem
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      backgroundColor: THEME.COLORS.NEUTRAL_GRAY_LIGHT
                    }
                  }}
                  onClick={() => {
                    setIsMobileMenuOpen(false);
                    handleMainItemClick(item.path);
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.PRIMARY,
                      fontWeight: THEME.TYPOGRAPHY.FONT_WEIGHT.SEMI_BOLD,
                      fontSize: '1.1rem',
                      color: THEME.COLORS.NEUTRAL_BLACK
                    }}
                  >
                    {item.title}
                  </Typography>
                </ListItem>
              )}
            </Box>
          ))}
        </List>
        {/* Footer */}
        <Box
          sx={{
            pt: 3,
            mt: 2,
            borderTop: `1px solid ${THEME.COLORS.NEUTRAL_GRAY_LIGHT}`
          }}
        >
          <Typography
            variant='caption'
            sx={{
              display: 'block',
              textAlign: 'center',
              color: THEME.COLORS.NEUTRAL_GRAY,
              fontFamily: THEME.TYPOGRAPHY.FONT_FAMILY.SECONDARY,
              fontSize: '0.75rem',
              lineHeight: 1.4
            }}
          >
            {`COPYRIGHT © ${currentYear} MarQ Capital LTD`}
            <br />
            ALL RIGHTS RESERVED
          </Typography>
        </Box>
      </Drawer>
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: THEME.Z_INDEX.HIGH
          }}
          onClick={toggleMobileMenu}
        />
      )}
    </>
  );
};

NavigationMenuMobile.propTypes = {
  textColor: PropTypes.string
};

export default NavigationMenuMobile;
